import type { MyResourceAccess } from "src/api/shares/types";
import {
  RoleWithPrivileges,
  UserProfessionalProfile,
} from "src/types/userTypes";

import { RoutingMetadata } from "./routing/types";
import { Tag } from "src/reusable/components/select/tag-select";

export type UtteranceType = "User" | "Assistant" | "System" | "Tool";

export enum ChatStyles {
  PRECISE = "precise",
  BALANCED = "balanced",
  CREATIVE = "creative",
}

export enum ContentType {
  TEXT = "text",
  THINK = "think",
  TOOL_CALL = "tool_call",
  TOOL_RESPONSE = "tool_response",
  USER_SELECTION_PROMPT = "user_selection_prompt",
  SUB_AGENT = "sub_agent",
}

export const contentTags: { [tag: string]: ContentType } = {
  think: ContentType.THINK,
  thought: ContentType.THINK, // Older model format, same rendering as <think>
  tool_call: ContentType.TOOL_CALL,
  function_call: ContentType.TOOL_CALL, // Some models use <function_call> instead of <tool_call>
  tool_response: ContentType.TOOL_RESPONSE,
  user_selection_prompt: ContentType.USER_SELECTION_PROMPT,
};

export type TMessageContentPart = {
  type: ContentType;
  text: string;
};

/** Option in a user selection prompt */
export interface UserSelectionOption {
  label: string;
  value: string;
  description?: string;
}

/** Data structure for user selection prompt tool response */
export interface UserSelectionPromptData {
  type: "user_selection_prompt";
  prompt_id: string;
  question: string;
  component_type: "dropdown" | "radio" | "buttons" | "checkbox";
  options: UserSelectionOption[];
  allow_custom: boolean;
  required: boolean;
}

/** Data structure for the mid-chat file upload prompt tool response */
export interface UserFileUploadPromptData {
  type: "user_file_upload_prompt";
  prompt_id: string;
  prompt: string;
  accepted_file_types: string[];
}

/** Field definition for an inline profile form (FTUX module 1). */
export interface UserProfileFormField {
  field: string;
  label: string;
  prompt: string;
  value: string;
}

/** Tool response that renders an inline multi-field profile form. */
export interface UserProfileFormPromptData {
  type: "user_profile_form";
  prompt_id: string;
  fields: UserProfileFormField[];
  submit_label: string;
  /** Set after the user submits the form (for history persistence). */
  user_form_submission?: {
    values: Record<string, string>;
    submitted_at: string;
  };
}

/** Agent-requested plan-template publication awaiting in-chat user approval.

 * On approval the frontend performs the write through the same
 * from-workspace promotion endpoint the manual "Add to Project" button
 * uses (`useAddFromWorkspace`), targeted at `folder_id`.
 */
export interface PlanTemplatePublishApprovalData {
  type: "plan_template_publish_approval";
  prompt_id: string;
  /** Staged ExternalAction id — the durable pending/resolved state the
   * card reads on reload (part-text edits don't survive persistence). */
  action_id?: number;
  /** Destination plans folder id (validated + write-checked by the tool). */
  folder_id: number;
  /** Workspace-relative path of the template file being published. */
  file_path: string;
  /** Destination plans-folder display path (e.g. "Equipment AI/plans"). */
  project_path: string;
  /** Template display name from the file's frontmatter. */
  template_name: string;
  overwrite: boolean;
  /** Set after user responds (for history persistence). */
  user_response?: {
    approved: boolean;
    submitted_at: string;
  };
  /** Publication outcome stored for the model after approval. */
  publish_result?: unknown;
}

/** Recency filter window for a web search query. */
export type RecencyFilter = "day" | "week" | "month" | "year";

/** Data structure for web search approval prompt (DOE 810 compliance) */
export interface WebSearchApprovalData {
  type: "web_search_approval";
  prompt_id: string;
  action_id: number;
  query: string;
  max_results: number;
  /** Admin-configured ceiling max_results can be raised to, at the time the
   * tool call ran. Absent on actions created before this field shipped. */
  max_results_limit?: number;
  recency?: RecencyFilter | null;
  compliance_warning: string;
  can_edit: boolean;
  provider?: string;
  /** Set after user responds (for history persistence) */
  user_response?: {
    approved: boolean;
    final_query?: string;
    submitted_at: string;
    /** Delivery to the agent session could not be confirmed; Retry is offered. */
    undelivered?: boolean;
    /** Recency/result-count overrides actually applied, if the search ran. */
    final_recency?: RecencyFilter | null;
    final_max_results?: number;
  };
}

/**
 * Image content for VLM (Vision Language Model) support.
 * Used for P&ID diagrams, engineering drawings, and document images.
 *
 * Images can be provided as:
 * - Base64 encoded data (for new uploads during a chat session)
 * - URL (for external images or artifact-based images from backend)
 * - Artifact ID (for persisted images stored in MinIO)
 *
 * When loading historical dialogues, artifact_id is resolved to a URL by the backend.
 */
export interface ImageContent {
  /** Base64 encoded image data (without the data: prefix) */
  data?: string;
  /** URL to an image (alternative to base64, or resolved artifact URL) */
  url?: string;
  /** MIME type of the image (e.g., "image/png", "image/jpeg") */
  media_type?: string;
  /** Detail level for vision models */
  detail?: "low" | "high" | "auto";
  /** Artifact ID for persisted images (backend resolves to URL) */
  artifact_id?: number;
}

/**
 * Utterance content supporting both text and images for VLM.
 */
export interface UtteranceContent {
  text: string;
  /** Optional list of images for VLM/multimodal support */
  images?: ImageContent[];
  /** Structured content parts for preserving tool_call/tool_response structure across turns */
  content_parts?: TMessageContentPart[];
}

interface BaseChatMessage {
  id?: string;
  parent_id?: string | null;
  type: UtteranceType;
  ranking_type?: number;
  feedback_value: number | null;
}

export interface ChatMessage extends BaseChatMessage {
  content: UtteranceContent;
}

export interface ChatMessageWithParts extends BaseChatMessage {
  contentParts: TMessageContentPart[];
  /** VLM images from dialogue history (artifact URLs resolved by backend) */
  images?: ImageContent[];
  /** Routing state snapshot at the time this message was created (streaming only) */
  routing?: RoutingMetadata;
}

/**
 * Top-level key on the turn-initial stream `metadata` event, present only
 * when the backend filtered the active agent's attached datasets for this
 * user (#30968). Absent means the user can read every attached dataset. Not
 * routing: a user-picked agent with no routing in play sends it too.
 */
export interface AgentDatasetAccess {
  agent_name: string | null;
  attached: number;
  accessible: number;
}

/**
 * Store shape: the wire payload plus the dialogue that produced it, so a value
 * restored from sessionStorage is never shown against a different dialogue.
 */
export interface AgentDatasetAccessState extends AgentDatasetAccess {
  dialogue_id: number | null;
}

export type IncomingSSEMessage = {
  id: string;
  parent_id: string;
  type: UtteranceType;
  ranking_type: number;
  feedback_value?: number | null;
  content: { text: string; content_parts?: TMessageContentPart[] };
  /** Content type from backend: "text", "think", "tool_call", "tool_response" */
  content_type?: ContentType;
};

/** Sub-agent event forwarded from backend via proxy transport */
export interface SubAgentEvent {
  subagent_dialogue_id: number;
  persona_name: string;
  /** Tool call id of the delegate_task that spawned this sub-agent — used to
   *  render the activity card inline, right after that tool-call part. */
  parent_tool_call_id?: string;
  /** "status" for lifecycle events, absent for forwarded inner events */
  type?: "status";
  status?: string;
  /** Wrapped original event from the sub-agent's AgentSession */
  inner?: {
    event: string;
    data: any;
    id?: string;
  };
}

/** A persisted sub-agent run from GET /agent/subagents/{id}, reconstructed so
 *  cards survive reload. Shape mirrors an activeSubAgents entry. */
export interface SubAgentRun {
  subagent_dialogue_id: number;
  parent_tool_call_id?: string | null;
  persona_name: string;
  status: string;
  events: SubAgentEvent[];
}

export interface TArtifact {
  id: string;
  messageId: string;
  type: any | null;
  isLoading: boolean;
  content: { text: any };
  parentId: string;
  toolName?: string;
  /** Unique ID for matching tool responses to tool calls during streaming */
  toolCallId?: string;
  /** Human-readable status message from the model (e.g., "Searching your documents for safety procedures...") */
  statusMessage?: string;
  crSearchResults?: any[];
}

export type AtomAssistQueryParams = {
  persona?: string | null;
};

export type AtomAssistConfigTags = {
  config_fk: number;
  tag_fk: number;
  tag: Tag;
};

export type PersonaAPIData = {
  id: number;
  slug: string;
  name: string;
  description: string;
  initial_prompt: string;
  system_prompt: string;
  model_version_fk: number;
  datasets: any[];
  atom_assist_config_tags: AtomAssistConfigTags[];
  config_toolkits: Toolkit[];
  config?: any;
  user?: User;
  config_data: {
    temperature?: number;
    top_search_results: number;
    model_id: number;
    tool_calling: boolean;
    max_tool_iterations: number;
  };
  remote_config_fk?: number;
  create_date: string;
  create_user_fk: number;
  // slot_dependencies: SlotDependency[]; // Replaced by dependency_statuses
  is_ready: boolean;
  dependency_statuses: IDependencyStatus[];
  is_slot_driven: boolean; // Added this field
  seeded: boolean;
  auto_routable: boolean;
  // Persona starring fields
  star_count: number;
  is_starred_by_user: boolean;
  // Resource-permission fields (agents share like datasets). Optional: an
  // older backend or an internal serialization may omit them.
  visibility_level?: "public" | "private";
  my_access?: MyResourceAccess;
  // Attached datasets the caller cannot read, stripped from `datasets`.
  datasets_hidden_count?: number;
  // LLM-generated enrichment fields
  is_enrichable: boolean; // Whether persona has enough data for enrichment
  tagline?: string;
  capabilities?: string[];
  best_for?: string;
  model_version: {
    inference_data_config: Record<string, unknown>;
    output_data_config: {
      output_type: string;
    };
    filename: string;
    config: Record<string, unknown>;
    training_env_id: number | null;
    storage_id: number | null;
    model_base_image: string;
    required_orchestrator_fk: number;
    id: number;
    is_deployed: boolean;
    model_fk: number;
    status_fk: string;
    status_message: string | null;
    version_number: number;
    create_date: string;
    user_id: number;
    parent_mlmodel_name: string | null;
  };
};

export interface User {
  id: number;
  username: string;
  roles: RoleWithPrivileges[];
  create_date: string;
  subscription_fk: string;
  email_validated: boolean;
  api_key_salt: any;
  user_display_name: string;
  active: boolean;
  profile: UserProfessionalProfile;
}

export interface PersonaAPIDataWithResource extends PersonaAPIData {
  remote_resource_name: string;
  remote_resource_id: number;
}

export interface Toolkit {
  name: string;
  description: string;
  deleted?: boolean;
}

// New interface for individual dependency status from backend
export interface IDependencyStatus {
  purpose_tag: string;
  slot_name: string;
  resource_type: string;
  is_required: boolean;
  is_resolved: boolean;
  resolved_resource_id?: number | null;
  resolved_resource_name?: string | null;
}

export interface AtomAssistSplashLocationState {
  initialUploadDialogueId?: number; // initialUserMessage exist if start of a new chat
  preloadDataset?: {
    id: number;
    name: string;
    description?: string;
  };
  preloadDatasets?: Array<{
    id: number;
    name: string;
    description?: string;
  }>;
  /** Document to preload for "Chat with Document" feature */
  preloadDocument?: {
    sdrId: number;
    fileName: string;
    datasetId: number;
  };
}
